import React from "react";
import Portal from "@reach/portal";
import classNames from "classnames";
import { ModalBodyProps, ModalFooterProps, ModalHeaderProps, ModalProps } from "./modal.d";
import { useCopies } from "shared/hooks/useCopies";
import ModalLayers from "shared/enums/ModalLayers";
import Button from "../button";
import ButtonGroup from "../button/components/buttonGroup";

const modalCountClassMap: Record<number, string> = {
  0: "",
  1: ModalLayers.FIRST,
  2: ModalLayers.SECOND,
  3: ModalLayers.THIRD,
  4: ModalLayers.FORTH
};

export const Modal = function (props: ModalProps) {
  const [modalCount, setModalCount] = React.useState<keyof typeof modalCountClassMap>(0);

  const computedClassNames = React.useMemo(
    () =>
      classNames(
        props.className,
        "modalCount modal",
        {
          [`modal--${props.size}`]: props.size
        },
        modalCountClassMap[modalCount]
      ),
    [props.className, props.size, modalCount]
  );

  React.useEffect(() => {
    setModalCount(c => {
      const currentModalCount = document.getElementsByClassName("modalCount").length;
      const nextModalCount = currentModalCount + 1;
      const isValidLayer = Object.keys(modalCountClassMap)
        .map(i => +i)
        .includes(nextModalCount);
      if (isValidLayer) {
        return nextModalCount;
      }

      return c;
    });
  }, []);

  React.useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "";
    };
  }, []);

  return (
    <Portal>
      <div className={computedClassNames}>
        <div className="modal_dialog" role="dialog" tabIndex={-1} aria-modal="true">
          {props.children}
        </div>
      </div>
    </Portal>
  );
};

export const ModalHeader = (props: ModalHeaderProps) => {
  const copies = useCopies();
  return (
    <React.Fragment>
      <div className={classNames(props.className, "modal_header")}>
        <div className="modal_header_left">{props.left}</div>
        <div className="modal_header_right">
          {props.right && <ButtonGroup>{props.right}</ButtonGroup>}
          {props.onClose && (
            <Button.Close
              onClick={props.onClose}
              title={copies.get("close")}
              data-dismiss="modal"
              testId="modal_close"
              aria-label={copies.get("close")}
            />
          )}
        </div>
      </div>
    </React.Fragment>
  );
};

////////////////////////////////////////////////////////////////////////

export const ModalBody = (props: ModalBodyProps) => {
  return (
    <div className={classNames(props.className, "modal_body")}>
      <div className="modal_body_container">{props.children}</div>
    </div>
  );
};

////////////////////////////////////////////////////////////////////////

export const ModalFooter = (props: ModalFooterProps) => {
  return (
    <div
      className={classNames(props.className, "modal_footer text-right", {
        hasFooterBorder: props.hasFooterBorder
      })}>
      <div className="modal_footer_container">{props.children}</div>
    </div>
  );
};
